import { Component, inject, effect } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { SiteConfigService } from './services/site-config.service';
import { PendoService } from './core/services/pendo.service';
import { MatomoService } from './core/services/matomo.service';
import { AuthService } from './core/services/auth.service';
import { BulkActionNotifierService } from './services/bulk-action-notifier.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, DialogModule, NotificationModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  title = 'Portal CMS';

  // Initialize SiteConfigService to apply theme colors
  private siteConfigService = inject(SiteConfigService);
  private pendoService = inject(PendoService);
  private matomoService = inject(MatomoService);
  private authService = inject(AuthService);
  private bulkActionNotifier = inject(BulkActionNotifierService);

  constructor() {
    // Initialize Matomo SPA tracking
    this.matomoService.initialize();

    // Listen for background bulk-action completions for the whole admin
    // session. Scoped here (not the admin layout) so toasts still arrive after
    // an admin navigates into their portal, which tears down the admin layout.
    effect(() => {
      const isAuthenticated = this.authService.isAuthenticated();
      const isAdmin = this.authService.isAdmin();
      const userData = this.authService.userResource.value();
      if (isAuthenticated && isAdmin && userData) {
        this.bulkActionNotifier.start();
      } else {
        this.bulkActionNotifier.stop();
      }
    });

    // Initialize Pendo and Matomo custom dimensions when user data is available
    effect(() => {
      const userData = this.authService.userResource.value();
      const isAuthenticated = this.authService.isAuthenticated();

      if (isAuthenticated && userData) {
        // Set Matomo custom dimensions (Analyst ID and Firm ID)
        this.matomoService.setUserDimensions(
          userData.analystId,
          userData.firmId,
        );

        // Initialize Pendo with visitor ID matching ARchitect/Portal format
        this.pendoService.initialize(
          {
            id: `${environment.pendoPrefix}-${userData.membershipUserId ?? userData.userId}`,
            email: userData.email,
            full_name: userData.name,
            role: userData.role,
            firm: userData.firmName,
          },
          userData.currentSiteId
            ? {
                id: userData.currentSiteId,
              }
            : undefined,
        );
      }
    });
  }
}
